/**
 * NMS GTM Event Manager
 * Centralized Google Tag Manager event handling for Neemans components
 *
 * Data Structure:
 * - window.neemans.analytics.shop     - Shop data (currency, name, etc.)
 * - window.neemans.analytics.product  - Current product (on product pages)
 * - window.neemans.analytics.cart     - Cart data (when cart has items)
 * - window.neemans.analytics.customer - Customer data (when logged in)
 * - window.neemans.analytics.page     - Page context (type, url, etc.)
 * - window.neemans.analytics.search   - Search data (on search pages)
 * - window.neemans.analytics.collection - Collection data (on collection pages)
 * - window.neemans.gtm.events         - This GTM manager instance
 *
 * Integrates with existing NMS component system and provides:
 * - Structured event tracking
 * - DataLayer management
 * - Event validation and debugging
 * - E-commerce event standardization
 * - Automatic add-to-cart tracking (variant:add events)
 */

class NMSGTMEvents {
  constructor() {
    this.dataLayer = window.dataLayer || [];
    this.debugMode = false;
    this.eventQueue = [];
    this.isInitialized = false;

    // Initialize GTM integration
    this.init();
  }

  init() {
    // Ensure dataLayer exists
    window.dataLayer = window.dataLayer || [];
    this.dataLayer = window.dataLayer;

    // Set up global namespace
    window.neemans = window.neemans || {};
    window.neemans.gtm = window.neemans.gtm || {};
    window.neemans.gtm.events = this;

    // Listen for NMS custom events and convert to GTM
    this.setupNMSEventListeners();

    // Process any queued events
    this.processEventQueue();

    this.isInitialized = true;
    this.log('GTM Event Manager initialized');
  }

  /**
   * Main method to push events to GTM dataLayer
   * @param {string} eventName - GTM event name
   * @param {object} eventData - Event data object
   * @param {object} options - Additional options
   */
  pushEvent(eventName, eventData = {}, options = {}) {
    if (!this.isInitialized) {
      this.eventQueue.push({ eventName, eventData, options });
      return;
    }

    // Validate required fields
    if (!eventName) {
      console.error('GTM Event: eventName is required');
      return;
    }

    // Build GTM event object
    const gtmEvent = {
      event: eventName,
      timestamp: new Date().toISOString(),
      page_title: document.title,
      page_url: window.location.href,
      ...eventData,
    };

    // Add user data if available
    const userData = this.getNeemansData('customer');
    if (userData) {
      gtmEvent.user_data = {
        customer_id: userData.id,
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        email_hash: userData.email ? this.hashEmail(userData.email) : null,
        customer_type: userData.orders_count > 0 ? 'returning' : 'new',
      };
    }

    // Add e-commerce context if available
    const ecommerceContext = this.getEcommerceContext();
    if (ecommerceContext) {
      gtmEvent.ecommerce = ecommerceContext;
    }

    // Push to dataLayer
    this.dataLayer.push(gtmEvent);
    this.log('GTM Event pushed:', gtmEvent);

    // Trigger callback if provided
    if (options.callback && typeof options.callback === 'function') {
      options.callback(gtmEvent);
    }
  }

  /**
   * E-commerce specific events
   */

  // Product view tracking
  trackProductView(product) {
    const productData = this.formatProductData(product);

    this.pushEvent('view_item', {
      currency: this.getNeemansData('shop.currency') || 'USD',
      value: productData.price, // Price already in decimal format
      items: [productData],
    });
  }

  // Add to cart tracking
  trackAddToCart(variant, quantity = 1, index = 0) {
    // Set quantity on variant before formatting
    const variantWithQuantity = { ...variant, quantity };
    const productData = this.formatVariantData(variantWithQuantity, index);

    this.pushEvent('add_to_cart', {
      currency: this.getNeemansData('shop.currency') || 'USD',
      value: productData.price * quantity, // Price already in decimal format
      items: [productData],
    });
  }

  // Remove from cart tracking
  trackRemoveFromCart(variant, quantity = 1, index = 0) {
    // Set quantity on variant before formatting
    const variantWithQuantity = { ...variant, quantity };
    const productData = this.formatVariantData(variantWithQuantity, index);

    this.pushEvent('remove_from_cart', {
      currency: this.getNeemansData('shop.currency') || 'USD',
      value: productData.price * quantity, // Price already in decimal format
      items: [productData],
    });
  }

  // Buy now tracking using SSR product data
  trackBuyNow(product, quantity = 1) {
    const selectedVariant = product.selectedVariant || {};
    const itemPrice = (selectedVariant.price || product.price || 0) / 100;
    const totalValue = itemPrice * quantity;

    const buyNowItem = {
      item_id: selectedVariant.id || product.id,
      item_name: product.product_name || product.title,
      currency: product.currency || 'INR',
      index: 0,
      item_brand: "Neeman's",
      item_category: product.product_category || product.type || 'Unknown',
      item_category2: 'NA',
      item_variant: selectedVariant.size || selectedVariant.title || 'Default',
      location_id: window.location.href,
      price: itemPrice,
      quantity: quantity,
    };

    this.pushEvent('buy_now', {
      currency: product.currency || 'INR',
      value: totalValue,
      items: [buyNowItem],
    });
  }

  // Cart view tracking
  trackCartView() {
    const cartData = this.getNeemansData('cart');

    if (!cartData) {
      this.log('No cart data available for tracking');
      return;
    }

    this.trackCartViewWithData(cartData);
  }

  // Cart view tracking with provided cart data
  trackCartViewWithData(cartData) {
    if (!cartData) {
      this.log('No cart data available for tracking');
      return;
    }

    // Calculate total value
    const totalValue = cartData.total_price / 100;

    // Prepare event data
    const eventData = {
      currency: cartData.currency || 'USD',
      value: totalValue,
    };

    // Add items only if cart has items
    if (cartData.items && cartData.items.length > 0) {
      // Format cart items for GTM
      const formattedItems = cartData.items.map((item, index) => ({
        item_id: item.product_id.toString(),
        item_name: item.product_title || item.title,
        item_category: item.product_type || 'Unknown',
        item_variant: item.variant_title || 'Default',
        price: item.price / 100,
        quantity: item.quantity,
        currency: cartData.currency || 'USD',
        index: index + 1,
      }));

      eventData.items = formattedItems;
    }

    this.pushEvent('view_cart', eventData);

    this.log('Cart view tracked:', {
      item_count: cartData.item_count,
      total_value: totalValue,
      has_items: !!(cartData.items && cartData.items.length > 0),
    });
  }

  // Purchase tracking
  trackPurchase(orderData) {
    this.pushEvent('purchase', {
      currency: orderData.currency || 'USD',
      transaction_id: orderData.order_number,
      value: orderData.total_price / 100,
      shipping: orderData.shipping_price / 100,
      tax: orderData.tax_price / 100,
      items: orderData.line_items.map(item => this.formatLineItemData(item)),
    });
  }

  // Search tracking
  trackSearch(searchTerm, resultsCount = null) {
    this.pushEvent('search', {
      search_term: searchTerm,
      results_count: resultsCount,
    });
  }

  /**
   * Custom business events
   */

  // Newsletter signup
  trackNewsletterSignup(email = null) {
    this.pushEvent('newsletter_signup', {
      method: 'website_form',
      email_hash: email ? this.hashEmail(email) : null,
    });
  }

  // Size guide interaction
  trackSizeGuideOpen(productType) {
    this.pushEvent('size_guide_interaction', {
      action: 'open',
      product_type: productType,
    });
  }

  // Wishlist actions
  trackWishlistAdd(productId) {
    this.pushEvent('add_to_wishlist', {
      action: 'add',
      item_id: productId,
    });
  }

  trackWishlistRemove(productId) {
    this.pushEvent('remove_from_wishlist', {
      action: 'remove',
      item_id: productId,
    });
  }

  /**
   * Integration with NMS component events
   */
  setupNMSEventListeners() {
    // Listen for all NMS custom events
    document.addEventListener('nms:buy-now-clicked', event => {
      // Use SSR product data from window.neemans.analytics.product
      const productData = this.getNeemansData('product');
      const quantity = event.detail.quantity || 1;

      if (productData) {
        this.trackBuyNow(productData, quantity);
      } else {
        console.warn('No product data available for Buy Now GTM tracking');
      }
    });

    // Theme system event: variant:add (successful add to cart)
    document.addEventListener('variant:add', event => {
      if (event.detail?.items?.length > 0) {
        const item = event.detail.items[0]; // First/primary item added

        // Track with GTM using our trackAddToCart method (GA4 e-commerce format)
        this.trackAddToCart(
          {
            id: item.id,
            product_id: item.product_id,
            title: item.title,
            variant_title: item.variant_title,
            product_type: item.product_type,
            price: item.price,
            product_title: item.product_title || item.title,
          },
          item.quantity || 1,
          0 // index
        );
      }
    });

    // Click event: data-nms-gtm-view-cart (cart view tracking)
    document.addEventListener('click', async event => {
      const target = event.target.closest('[data-nms-gtm-view-cart]');
      if (!target) return;

      try {
        // Fetch fresh cart data from Shopify cart.js API
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        if (cart) {
          this.trackCartViewWithData(cart);
        }
      } catch (error) {
        // Silent fail - only log in debug mode
        this.log('Failed to fetch cart data for GTM tracking:', error);
      }
    });
  }

  /**
   * Data formatting utilities
   */

  formatProductData(product) {
    const rawPrice = product.price || product.selected_or_first_available_variant?.price || 0;

    return {
      item_id: product.id.toString(),
      item_name: product.title,
      item_category: product.type || 'Unknown',
      item_variant: product.selected_or_first_available_variant?.title || 'Default',
      price: rawPrice / 100, // Convert cents to decimal format
      currency: this.getNeemansData('shop.currency') || 'USD',
    };
  }

  formatVariantData(variant, index = 0) {
    return {
      item_id: variant.product_id.toString(),
      item_name: variant.product_title || variant.title,
      item_category: variant.product_type || 'Unknown',
      item_variant: variant.variant_title || 'Default',
      price: variant.price / 100, // Convert cents to decimal format
      quantity: variant.quantity || 1,
      currency: this.getNeemansData('shop.currency') || 'USD',
      index: index + 1,
    };
  }

  formatLineItemData(lineItem) {
    return {
      item_id: lineItem.product_id.toString(),
      item_name: lineItem.title,
      item_variant: lineItem.variant_title,
      price: lineItem.price / 100, // Convert cents to decimal format
      quantity: lineItem.quantity,
      currency: this.getNeemansData('shop.currency') || 'USD',
    };
  }

  /**
   * Utility methods
   */

  // Get data from window.neemans.analytics namespace
  getNeemansData(key) {
    const keys = key.split('.');
    let data = window.neemans?.analytics;

    for (const k of keys) {
      data = data?.[k];
      if (data === undefined) return null;
    }

    return data;
  }

  getEcommerceContext() {
    // Return current e-commerce context if available
    const cart = this.getNeemansData('cart');
    const product = this.getNeemansData('product');

    // Only include ecommerce context if cart has items or we're on a product page
    if ((cart && cart.item_count > 0) || product) {
      return {
        cart_total: cart?.total_price / 100 || 0,
        cart_item_count: cart?.item_count || 0,
        current_product_id: product?.id || null,
      };
    }

    return null;
  }

  hashEmail(email) {
    // Simple hash for privacy compliance
    // In production, consider using a proper hashing library
    let hash = 0;
    for (let i = 0; i < email.length; i++) {
      const char = email.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString();
  }

  processEventQueue() {
    while (this.eventQueue.length > 0) {
      const { eventName, eventData, options } = this.eventQueue.shift();
      this.pushEvent(eventName, eventData, options);
    }
  }

  log(...args) {
    if (this.debugMode) {
      console.log('[NMS GTM]', ...args);
    }
  }
}

// Initialize GTM Event Manager when DOM is ready
function initNMSGTMEvents() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new NMSGTMEvents();
    });
  } else {
    new NMSGTMEvents();
  }
}

// Auto-initialize
initNMSGTMEvents();
